import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Star, Search, Quote } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const TestimonialsManagementPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    content: '',
    rating: 5,
    image_url: '',
    featured: false
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await API.get('/admin/testimonials');
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTestimonial) {
        await API.put(`/admin/testimonials/${editingTestimonial.id}`, formData);
        toast.success('Testimonial updated!');
      } else {
        await API.post('/admin/testimonials', formData);
        toast.success('Testimonial created!');
      }
      setShowForm(false);
      setEditingTestimonial(null);
      resetForm();
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      role: testimonial.role,
      content: testimonial.content,
      rating: testimonial.rating,
      image_url: testimonial.image_url || '',
      featured: testimonial.featured
    });
    setShowForm(true);
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await API.delete(`/admin/testimonials/${testimonialId}`);
      toast.success('Testimonial deleted!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const toggleFeatured = async (testimonial) => {
    try {
      await API.put(`/admin/testimonials/${testimonial.id}`, {
        ...testimonial,
        featured: !testimonial.featured
      });
      toast.success(testimonial.featured ? 'Removed from featured' : 'Added to featured!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      content: '',
      rating: 5,
      image_url: '',
      featured: false
    });
  };

  const filteredTestimonials = testimonials.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="testimonials-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">Testimonials</h1>
          <p className="text-[#57534E]">Manage client testimonials shown on the website.</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingTestimonial(null); resetForm(); }}
          className="flex items-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
          data-testid="add-testimonial-btn"
        >
          <Plus className="w-5 h-5" />
          Add Testimonial
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
        <input
          type="text"
          placeholder="Search testimonials..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10"
          data-testid="search-testimonials"
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto p-6" data-testid="testimonial-form">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">
              {editingTestimonial ? 'Edit Testimonial' : 'New Testimonial'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Client Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full"
                  placeholder="John Smith"
                  data-testid="testimonial-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Role / Description</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  required
                  className="w-full"
                  placeholder="e.g., Amateur Golfer, 12 Handicap"
                  data-testid="testimonial-role"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Testimonial</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  required
                  rows={4}
                  className="w-full"
                  placeholder="What the client said about their experience..."
                  data-testid="testimonial-content"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating }))}
                      className={`p-2 ${formData.rating >= rating ? 'text-[#BC562B]' : 'text-[#E7E5E4]'}`}
                    >
                      <Star className="w-6 h-6 fill-current" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Photo URL (optional)</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  className="w-full"
                  placeholder="https://example.com/photo.jpg"
                  data-testid="testimonial-image"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-5 h-5"
                  data-testid="testimonial-featured"
                />
                <label htmlFor="featured" className="text-[#1A1A1A]">Featured on homepage</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
                  data-testid="save-testimonial-btn"
                >
                  {editingTestimonial ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingTestimonial(null); }}
                  className="px-6 py-3 border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Testimonials List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredTestimonials.length === 0 ? (
        <div className="text-center py-20 bg-white border border-[#E7E5E4]">
          <Quote className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
          <p className="text-[#737373]">
            {searchTerm ? 'No testimonials match your search' : 'No testimonials yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredTestimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`bg-white border p-6 ${
                testimonial.featured 
                  ? 'border-[#BC562B]/30 border-l-4 border-l-[#BC562B]' 
                  : 'border-[#E7E5E4]'
              }`}
              data-testid={`testimonial-${index}`}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  {testimonial.image_url ? (
                    <img 
                      src={testimonial.image_url} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-[#2C5530]/10 rounded-full flex items-center justify-center">
                      <span className="text-[#2C5530] font-semibold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A]">{testimonial.name}</h3>
                    <p className="text-sm text-[#737373]">{testimonial.role}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleFeatured(testimonial)}
                    className={`p-2 ${testimonial.featured ? 'text-[#BC562B]' : 'text-[#E7E5E4]'} hover:bg-[#F2F0E9] transition-colors`}
                    title={testimonial.featured ? 'Remove from featured' : 'Add to featured'}
                    data-testid={`toggle-featured-${index}`}
                  >
                    <Star className="w-5 h-5 fill-current" />
                  </button>
                  <button
                    onClick={() => handleEdit(testimonial)}
                    className="p-2 text-[#57534E] hover:bg-[#F2F0E9] transition-colors"
                    title="Edit"
                    data-testid={`edit-testimonial-${index}`}
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial.id)}
                    className="p-2 text-[#BC562B] hover:bg-red-50 transition-colors"
                    title="Delete"
                    data-testid={`delete-testimonial-${index}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex mb-3">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star 
                    key={star} 
                    className={`w-4 h-4 ${star <= testimonial.rating ? 'text-[#BC562B] fill-current' : 'text-[#E7E5E4]'}`} 
                  />
                ))}
              </div>

              <p className="text-[#57534E] italic">"{testimonial.content}"</p>
              
              <p className="text-xs text-[#737373] mt-4">
                Added {formatDate(testimonial.created_at)}
                {testimonial.featured && (
                  <span className="ml-2 text-[#BC562B]">• Featured</span>
                )}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialsManagementPage;
